import React, { useState, useEffect } from 'react';
import { format, differenceInHours, differenceInDays } from 'date-fns';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Clock, Car, Tag, Check, Zap, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

export const BookingForm = ({ listing, onSubmit, loading }) => {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [vehicleSize, setVehicleSize] = useState('medium');
  const [vehicleRego, setVehicleRego] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [checkingPromo, setCheckingPromo] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState('weekly');
  const [recurrenceCount, setRecurrenceCount] = useState(4);
  const [dynamicPricing, setDynamicPricing] = useState(null);

  const SERVICE_FEE_RATE = 0.15; // 15% service fee for buyers
  const RECURRING_DISCOUNT = 0.10; // 10% discount for recurring bookings

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('session_token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const applyPromoCode = async () => {
    if (!promoCode.trim()) return;
    setCheckingPromo(true);
    try {
      const headers = getAuthHeaders();
      const res = await axios.post(`${API}/promo/validate`, { code: promoCode }, { withCredentials: true, headers });
      setAppliedPromo(res.data);
      toast.success(`Promo applied: ${res.data.discount_percent}% off!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid promo code');
      setAppliedPromo(null);
    } finally {
      setCheckingPromo(false);
    }
  };

  const calculatePrice = () => {
    if (!startDate || !endDate) return null;

    const start = new Date(startDate);
    start.setHours(parseInt(startTime.split(':')[0]), parseInt(startTime.split(':')[1]));
    
    const end = new Date(endDate);
    end.setHours(parseInt(endTime.split(':')[0]), parseInt(endTime.split(':')[1]));

    // Calculate duration in hours (as decimal)
    const durationMs = end - start;
    const durationHours = durationMs / (1000 * 60 * 60);
    const days = differenceInDays(end, start);

    if (durationHours < 0) return null;

    let basePrice;
    let breakdown;

    // Use daily rate if 8+ hours
    if (durationHours >= 8 && listing.price_daily) {
      const effectiveDays = Math.max(1, days);
      basePrice = listing.price_daily * effectiveDays;
      breakdown = `${effectiveDays} day${effectiveDays > 1 ? 's' : ''} @ $${listing.price_daily}/day`;
    } else {
      // Minimum 30 minutes = half hourly rate
      const effectiveHours = Math.max(0.5, durationHours);
      basePrice = listing.price_hourly * effectiveHours;
      
      // Format breakdown text
      if (effectiveHours < 1) {
        breakdown = `30 min @ $${(listing.price_hourly / 2).toFixed(2)} (half hourly rate)`;
      } else if (effectiveHours === Math.floor(effectiveHours)) {
        breakdown = `${effectiveHours} hour${effectiveHours > 1 ? 's' : ''} @ $${listing.price_hourly}/hr`;
      } else {
        breakdown = `${effectiveHours.toFixed(1)} hours @ $${listing.price_hourly}/hr`;
      }
    }

    // Apply dynamic pricing multiplier (from backend)
    const multiplier = dynamicPricing?.multiplier || 1;
    const adjustedBase = basePrice * multiplier;
    const surgeAmount = adjustedBase - basePrice;

    const serviceFee = adjustedBase * SERVICE_FEE_RATE;
    const recurringDiscount = isRecurring ? (adjustedBase * RECURRING_DISCOUNT) : 0;
    const discount = appliedPromo ? (adjustedBase * appliedPromo.discount_percent / 100) : 0;
    const singleTotal = adjustedBase + serviceFee - discount - recurringDiscount;
    const total = isRecurring ? singleTotal * recurrenceCount : singleTotal;

    return {
      basePrice: basePrice.toFixed(2),
      adjustedBase: adjustedBase.toFixed(2),
      surgeAmount: surgeAmount.toFixed(2),
      multiplier,
      pricingReason: dynamicPricing?.pricing_reason,
      serviceFee: serviceFee.toFixed(2),
      recurringDiscount: recurringDiscount.toFixed(2),
      discount: discount.toFixed(2),
      singleTotal: singleTotal.toFixed(2),
      total: total.toFixed(2),
      breakdown,
      sessions: isRecurring ? recurrenceCount : 1
    };
  };

  // Fetch dynamic pricing whenever date/time changes
  useEffect(() => {
    if (!startDate || !endDate) {
      setDynamicPricing(null);
      return;
    }
    const start = new Date(startDate);
    start.setHours(parseInt(startTime.split(':')[0]), parseInt(startTime.split(':')[1]));
    const end = new Date(endDate);
    end.setHours(parseInt(endTime.split(':')[0]), parseInt(endTime.split(':')[1]));
    if (end <= start) {
      setDynamicPricing(null);
      return;
    }
    const controller = new AbortController();
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/pricing/estimate`, {
          params: {
            listing_id: listing.listing_id,
            start_time: start.toISOString(),
            end_time: end.toISOString(),
          },
          signal: controller.signal,
        });
        setDynamicPricing(res.data);
      } catch (err) {
        if (!axios.isCancel?.(err)) {
          setDynamicPricing(null);
        }
      }
    }, 350);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [startDate, endDate, startTime, endTime, listing.listing_id]);

  const price = calculatePrice();

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!startDate || !endDate) {
      toast.error('Please select dates');
      return;
    }
    if (!vehicleRego.trim()) {
      toast.error('Vehicle registration is required for booking verification');
      return;
    }
    if (vehicleRego.trim().length < 2) {
      toast.error('Please enter a valid vehicle registration');
      return;
    }
    
    const start = new Date(startDate);
    start.setHours(parseInt(startTime.split(':')[0]), parseInt(startTime.split(':')[1]));
    
    const end = new Date(endDate);
    end.setHours(parseInt(endTime.split(':')[0]), parseInt(endTime.split(':')[1]));

    const bookingData = {
      listing_id: listing.listing_id,
      start_time: start.toISOString(),
      end_time: end.toISOString(),
      vehicle_size: vehicleSize,
      vehicle_registration: vehicleRego.toUpperCase().trim(),
      promo_code: appliedPromo?.code || null
    };

    if (isRecurring) {
      bookingData.is_recurring = true;
      bookingData.recurrence_type = recurrenceType;
      bookingData.recurrence_count = recurrenceCount;
    }

    onSubmit(bookingData);
  };

  const timeOptions = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {
      const time = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      timeOptions.push(time);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="booking-form">
      {/* Date Selection */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Start Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !startDate && "text-muted-foreground"
                )}
                data-testid="start-date-btn"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDate ? format(startDate, "MMM d, yyyy") : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={setStartDate}
                disabled={(date) => date < new Date()}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label>End Date</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !endDate && "text-muted-foreground"
                )}
                data-testid="end-date-btn"
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {endDate ? format(endDate, "MMM d, yyyy") : "Select date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={setEndDate}
                disabled={(date) => date < (startDate || new Date())}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Time Selection */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Start Time
          </Label>
          <Select value={startTime} onValueChange={setStartTime}>
            <SelectTrigger data-testid="start-time-select">
              <SelectValue placeholder="Select time" />
            </SelectTrigger>
            <SelectContent>
              {timeOptions.map(time => (
                <SelectItem key={time} value={time}>{time}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            End Time
          </Label>
          <Select value={endTime} onValueChange={setEndTime}>
            <SelectTrigger data-testid="end-time-select">
              <SelectValue placeholder="Select time" />
            </SelectTrigger>
            <SelectContent>
              {timeOptions.map(time => (
                <SelectItem key={time} value={time}>{time}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Vehicle Details */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Car className="w-4 h-4" />
          Vehicle Size
        </Label>
        <Select value={vehicleSize} onValueChange={setVehicleSize}>
          <SelectTrigger data-testid="vehicle-size-select">
            <SelectValue placeholder="Select size" />
          </SelectTrigger>
          <SelectContent>
            {listing.vehicle_sizes?.map(size => (
              <SelectItem key={size} value={size} className="capitalize">{size}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Car className="w-4 h-4" />
          Vehicle Registration <span className="text-red-500">*</span>
        </Label>
        <Input
          placeholder="e.g., ABC 123"
          value={vehicleRego}
          onChange={(e) => setVehicleRego(e.target.value.toUpperCase())}
          data-testid="vehicle-rego-input"
          required
        />
        <p className="text-xs text-slate-500">
          🔒 Required for verification - helps hosts confirm your booking
        </p>
      </div>

      {/* Promo Code */}
      <div className="space-y-2">
        <Label className="flex items-center gap-2">
          <Tag className="w-4 h-4" />
          Promo Code
        </Label>
        <div className="flex gap-2">
          <Input
            placeholder="Enter code"
            value={promoCode}
            onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
            disabled={!!appliedPromo}
            className="flex-1"
            data-testid="promo-code-input"
          />
          {appliedPromo ? (
            <Button type="button" variant="outline" className="text-green-600" disabled>
              <Check className="w-4 h-4 mr-1" /> Applied
            </Button>
          ) : (
            <Button type="button" variant="outline" onClick={applyPromoCode} disabled={checkingPromo || !promoCode}>
              {checkingPromo ? '...' : 'Apply'}
            </Button>
          )}
        </div>
      </div>

      {/* Recurring Booking Option */}
      <div className="space-y-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="recurring"
            checked={isRecurring}
            onChange={(e) => setIsRecurring(e.target.checked)}
            className="w-5 h-5 rounded border-blue-300 text-blue-600 focus:ring-blue-500"
          />
          <Label htmlFor="recurring" className="cursor-pointer flex items-center gap-2">
            <span className="text-blue-600">🔄</span>
            <span>Make this a recurring booking</span>
            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">10% off!</span>
          </Label>
        </div>
        
        {isRecurring && (
          <div className="grid grid-cols-2 gap-3 mt-3 pl-8">
            <div className="space-y-1">
              <Label className="text-sm text-slate-600">Frequency</Label>
              <Select value={recurrenceType} onValueChange={setRecurrenceType}>
                <SelectTrigger className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-sm text-slate-600">How many?</Label>
              <Select value={recurrenceCount.toString()} onValueChange={(v) => setRecurrenceCount(parseInt(v))}>
                <SelectTrigger className="bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2, 4, 8, 12, 24, 52].map(n => (
                    <SelectItem key={n} value={n.toString()}>{n} {recurrenceType === 'weekly' ? 'weeks' : 'months'}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {/* Price Breakdown */}
      {price && (
        <div className="bg-slate-50 rounded-xl p-4 space-y-2" data-testid="price-breakdown">
          {/* Surge / Dynamic Pricing Banner */}
          {price.multiplier && price.multiplier !== 1 && (
            <div
              className={cn(
                "flex items-start gap-2 -mx-4 -mt-4 mb-3 px-4 py-2.5 rounded-t-xl",
                price.multiplier > 1
                  ? "bg-amber-50 border-b border-amber-200"
                  : "bg-blue-50 border-b border-blue-200"
              )}
              data-testid="surge-pricing-badge"
            >
              {price.multiplier > 1 ? (
                <Zap className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              ) : (
                <TrendingDown className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className={cn(
                    "text-xs font-semibold",
                    price.multiplier > 1 ? "text-amber-800" : "text-blue-800"
                  )}>
                    {price.multiplier > 1 ? 'Surge Pricing' : 'Off-peak Discount'} · {price.multiplier}×
                  </p>
                  {price.multiplier > 1 ? (
                    <span className="text-[11px] text-amber-700">+${price.surgeAmount}</span>
                  ) : (
                    <span className="text-[11px] text-blue-700">Save ${Math.abs(parseFloat(price.surgeAmount)).toFixed(2)}</span>
                  )}
                </div>
                {price.pricingReason && (
                  <p className={cn(
                    "text-[11px] leading-snug mt-0.5",
                    price.multiplier > 1 ? "text-amber-700" : "text-blue-700"
                  )}>
                    {price.pricingReason}
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-between text-sm text-slate-600">
            <span>{price.breakdown}</span>
            <span className={price.multiplier !== 1 ? "line-through text-slate-400" : ""}>${price.basePrice}</span>
          </div>
          {price.multiplier !== 1 && (
            <div className="flex justify-between text-sm text-slate-700">
              <span>Adjusted price ({price.multiplier}×)</span>
              <span className="font-medium">${price.adjustedBase}</span>
            </div>
          )}
          <div className="flex justify-between text-xs text-slate-400">
            <span>Service fee <span className="text-[10px]">(15%)</span></span>
            <span>${price.serviceFee}</span>
          </div>
          {isRecurring && (
            <div className="flex justify-between text-sm text-green-600">
              <span>Recurring discount (10% off)</span>
              <span>-${price.recurringDiscount}</span>
            </div>
          )}
          {appliedPromo && (
            <div className="flex justify-between text-sm text-green-600">
              <span>Promo ({appliedPromo.discount_percent}% off)</span>
              <span>-${price.discount}</span>
            </div>
          )}
          {isRecurring && (
            <>
              <div className="flex justify-between text-sm text-slate-600 border-t border-slate-200 pt-2">
                <span>Per session</span>
                <span>${price.singleTotal}</span>
              </div>
              <div className="flex justify-between text-xs text-slate-500">
                <span>× {price.sessions} {recurrenceType} sessions</span>
              </div>
            </>
          )}
          <div className="border-t border-slate-200 pt-2 flex justify-between font-semibold text-lg">
            <span>Total</span>
            <span className="text-[#5ebc67]">${price.total}</span>
          </div>
        </div>
      )}

      {/* Saved-card / overstay disclosure (legally required when saving payment methods off-session) */}
      <div className="text-[11px] text-slate-500 leading-relaxed bg-slate-50 rounded-lg p-3 border border-slate-200" data-testid="overstay-disclosure">
        Your card will be securely saved by Stripe. If you don't tap <strong>"I've left"</strong> within 5 minutes of your booking ending,
        an overstay fee of <strong>$10 per 30 minutes</strong> (capped at $480/24h) will be charged automatically.
        You can stop further charges anytime by checking out from your bookings page.
      </div>

      {/* Submit */}
      <Button
        type="submit"
        className="w-full bg-[#5ebc67] hover:bg-[#4da855] text-white rounded-full py-6 text-lg font-semibold"
        disabled={!startDate || !endDate || !price || loading}
        data-testid="book-now-btn"
      >
        {loading ? 'Processing...' : 'Book Now'}
      </Button>
    </form>
  );
};

export default BookingForm;
